import React, { useMemo, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { DashboardProvider } from "@/contexts/DashboardContext";
import useSession from "@/v2/hooks/supabase/useSession";
import Logo from "@/components/Logo";
import DashboardSidebar from "@/components/domains/dashboard/DashboardSidebar";
import SuperadminSidebar from "@/components/superadmin/SuperadminSidebar";
import NotificationBell from "@/components/domains/notifications/NotificationBell";
import AuthButton from "@/components/AuthButton";
import { useIsMobile } from "@/hooks/use-mobile";

const AppLayout: React.FC = React.memo(() => {
  const { user, loading: isLoading } = useSession();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isEditorFullscreen = useMemo(() => {
    return location.pathname.startsWith('/dashboard/portfolio/case-study');
  }, [location.pathname]);

  // Memoize sidebar selection to prevent unnecessary re-renders
  const { isSuperadminRoute, SidebarComponent, contentKey } = useMemo(() => {
    const isSuperadmin = location.pathname.startsWith('/superadmin');
    return {
      isSuperadminRoute: isSuperadmin,
      SidebarComponent: isSuperadmin ? SuperadminSidebar : DashboardSidebar,
      contentKey: `${isSuperadmin ? 'superadmin' : 'dashboard'}-main`
    };
  }, [location.pathname]);

  const userMetadata = useMemo(
    () => ((user?.user_metadata ?? {}) as Record<string, string | undefined>),
    [user?.user_metadata]
  );
  const userName =
    userMetadata.full_name ||
    userMetadata.display_name ||
    userMetadata.name ||
    userMetadata.username ||
    undefined;

  // Add dashboard-page class to body for CSS styling
  useEffect(() => {
    const isDashboardPage = location.pathname.startsWith('/dashboard') || 
                           location.pathname.startsWith('/superadmin');
    
    if (isDashboardPage) {
      document.body.classList.add('dashboard-page');
    } else {
      document.body.classList.remove('dashboard-page');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('dashboard-page');
    };
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#EBECF0] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <ProfileProvider>
      <DashboardProvider>
        <div className="min-h-screen bg-[#EBECF0] transition-all duration-300 ease-in-out">
          {/* Header */}
          <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm py-3 transition-all duration-300">
            <div className="container px-3 sm:px-4 flex justify-between items-center">
              <Logo />
              <div className="flex items-center space-x-3 sm:space-x-4">
                {user && <NotificationBell />}
                <AuthButton />
              </div>
            </div>
          </header>

          {/* Main Content */}
          <div className="w-full px-3 sm:px-4 py-4 sm:py-6 container mx-auto relative transition-all duration-300 h-[calc(100vh-73px)]">
            {isEditorFullscreen ? (
              // Full-width content without sidebar
              <div className="transition-all duration-300 h-full" key={contentKey}>
                <Outlet />
              </div>
            ) : isMobile ? (
              <div className="lg:hidden transition-all duration-300 h-full flex flex-col">
                <SidebarComponent />
                <div className="mt-2 flex-1 overflow-auto">
                  <Outlet />
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-6 transition-all duration-300 h-full">
                <div className="w-64 flex-shrink-0 transition-all duration-300">
                  <div className="sticky top-24">
                    <SidebarComponent />
                  </div>
                </div>
                <div className="flex-1 transition-all duration-300 overflow-auto h-full" key={contentKey}>
                  <Outlet />
                </div>
              </div>
            )}
          </div>
        </div>
      </DashboardProvider>
    </ProfileProvider>
  );
});

export default AppLayout;
